import { FormEvent, useCallback, useEffect, useMemo, useState } from 'react'
import { Loader2, PlusCircle } from 'lucide-react'
import { userClient } from '../../lib/userClient'
import { WaybookEvent, WaybookTrip } from '../../types'
import { useUserTrips } from '../../hooks/useUserTrips'

export default function UserEventsPage() {
  const { trips } = useUserTrips({ autoFetch: true, limit: 100 })
  const [events, setEvents] = useState<WaybookEvent[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [showForm, setShowForm] = useState(false)
  const [tripId, setTripId] = useState('')
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [formError, setFormError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const fetchEvents = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await userClient.getEvents({ limit: 50 })
      const payload = (response.data?.data || []) as WaybookEvent[]
      setEvents(Array.isArray(payload) ? payload : [])
    } catch (err: any) {
      const message =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        'Failed to load events'
      setError(message)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchEvents()
  }, [fetchEvents])

  const orderedEvents = useMemo(
    () => events.slice().sort((a, b) => new Date(a.start_date).getTime() - new Date(b.start_date).getTime()),
    [events]
  )

  const handleCreateEvent = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!tripId) {
      setFormError('Select a trip for this event')
      return
    }
    if (!title.trim()) {
      setFormError('Title is required')
      return
    }
    if (!startDate) {
      setFormError('Start date is required')
      return
    }

    setSubmitting(true)
    setFormError(null)

    try {
      await userClient.createTripEvent({
        trip_id: tripId,
        title: title.trim(),
        description: description.trim() || undefined,
        start_date: startDate,
        end_date: endDate || undefined,
      })
      setTitle('')
      setDescription('')
      setStartDate('')
      setEndDate('')
      setTripId('')
      setShowForm(false)
      fetchEvents()
    } catch (err: any) {
      const message =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        'Failed to create event'
      setFormError(message)
    } finally {
      setSubmitting(false)
    }
  }

  const tripOptions = useMemo(
    () => trips.slice().sort((a: WaybookTrip, b: WaybookTrip) => a.title?.localeCompare(b.title || '') || 0),
    [trips]
  )

  return (
    <div className="space-y-8">
      <section className="bg-white border border-gray-200 rounded-xl shadow-sm p-6">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <PlusCircle className="h-5 w-5" />
              Schedule an event
            </h2>
            <p className="text-sm text-gray-600">
              Create trip events through <code>POST /events</code>. Choose the parent trip first.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setShowForm((value) => !value)}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition"
          >
            {showForm ? 'Hide form' : 'Create event'}
          </button>
        </div>

        {showForm && (
          <form onSubmit={handleCreateEvent} className="mt-6 space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="event-trip">
                  Trip
                </label>
                <select
                  id="event-trip"
                  className="input w-full"
                  value={tripId}
                  onChange={(event) => setTripId(event.target.value)}
                >
                  <option value="">Select a trip</option>
                  {tripOptions.map((trip) => (
                    <option key={trip.id} value={trip.id}>
                      {trip.title || trip.id}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="event-title">
                  Title
                </label>
                <input
                  id="event-title"
                  className="input w-full"
                  value={title}
                  onChange={(event) => setTitle(event.target.value)}
                  placeholder="Dinner in Rome"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="event-description">
                Description (optional)
              </label>
              <textarea
                id="event-description"
                className="input w-full min-h-[120px]"
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                placeholder="Add context or details for participants"
              />
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="event-start">
                  Start date
                </label>
                <input
                  id="event-start"
                  type="datetime-local"
                  className="input w-full"
                  value={startDate}
                  onChange={(event) => setStartDate(event.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="event-end">
                  End date
                </label>
                <input
                  id="event-end"
                  type="datetime-local"
                  className="input w-full"
                  value={endDate}
                  onChange={(event) => setEndDate(event.target.value)}
                />
              </div>
            </div>

            {formError && (
              <div className="rounded-lg border border-red-100 bg-red-50 px-3 py-2 text-sm text-red-700">
                {formError}
              </div>
            )}

            <div>
              <button
                type="submit"
                disabled={submitting}
                className="btn-primary inline-flex items-center gap-2"
              >
                {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                Save event
              </button>
            </div>
          </form>
        )}
      </section>

      <section className="bg-white border border-gray-200 rounded-xl shadow-sm p-6">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Upcoming events</h2>
            <p className="text-sm text-gray-600">Listing data returned from <code>GET /events</code>.</p>
          </div>
        </div>

        <div className="mt-6 space-y-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-gray-500" />
            </div>
          ) : error ? (
            <div className="rounded-lg border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          ) : orderedEvents.length === 0 ? (
            <div className="rounded-lg border border-dashed border-gray-200 px-6 py-10 text-center text-sm text-gray-500">
              No events yet. Create one to start planning.
            </div>
          ) : (
            orderedEvents.map((event) => (
              <article key={event.id} className="border border-gray-200 rounded-lg p-4 bg-white shadow-sm">
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <span className="uppercase font-semibold tracking-wide">{event.status || 'scheduled'}</span>
                  <span>{new Date(event.start_date).toLocaleString()}</span>
                </div>
                <h3 className="mt-2 text-base font-semibold text-gray-900">{event.title}</h3>
                {event.description && (
                  <p className="mt-2 text-sm text-gray-600 whitespace-pre-wrap">{event.description}</p>
                )}
                <div className="mt-3 text-xs text-gray-500 flex flex-wrap gap-3">
                  {event.trip_id && <span>Trip: {event.trip_id}</span>}
                  {event.end_date && <span>Ends: {new Date(event.end_date).toLocaleString()}</span>}
                </div>
              </article>
            ))
          )}
        </div>
      </section>
    </div>
  )
}
